import React, { ReactNode } from 'react';
import { observer } from 'mobx-react-lite';
import { MomentModel } from '../../entities';
import { TrainerTranscriptsState } from './trainerTranscriptsState';
import { ArrowRightSVGIcon, CloseSVGIcon } from '../svg';
import { generateTranscriptText } from './trainerTranscriptTextBlock';

type Props = {
  ownerModel: TrainerTranscriptsState,
  moment: MomentModel
}

export const TrainerTranscriptBlock = observer((props: Props) => {

  const { moment, ownerModel } = props;
  const { description } = moment;
  const active = ownerModel.isTranscriptMomentActive(moment);

  const redactions = ownerModel.redaction.getTranscriptRedactions(moment);
  const selection = ownerModel.redaction.transcriptSelection?.momentId === moment.id
    ? ownerModel.redaction.transcriptSelection
    : null;

  let text: ReactNode = (description && moment.wordConfidenceNorm && moment.avgConfidence !== 1)
    ? generateTranscriptText(description, moment.wordConfidenceNorm)
    : description;

  if (description && redactions.length > 0) {
    const parts: ReactNode[] = [];
    let cursor = 0;

    redactions.forEach(({ moment: redactionMoment, startOffset, endOffset }) => {
      const start = Math.max(cursor, Math.min(startOffset, description.length));
      const end = Math.max(start, Math.min(endOffset, description.length));
      if (end <= cursor)
        return;

      parts.push(description.slice(cursor, start));
      parts.push(
        <span className="transcript-redacted-text" key={`${redactionMoment.id}-${start}`}>
          <span>{description.slice(start, end)}</span>
          {ownerModel.redaction.canEdit && <button
            type="button"
            aria-label="Undo redaction"
            title="Undo redaction"
            onClick={evt => {
              evt.stopPropagation();
              ownerModel.redaction.undoRedaction(redactionMoment);
            }}>
            <CloseSVGIcon />
          </button>}
        </span>
      );
      cursor = end;
    });
    parts.push(description.slice(cursor));
    text = parts;
  }

  const handleTextSelection = (evt: React.MouseEvent<HTMLDivElement>) => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0 || selection.isCollapsed)
      return ownerModel.redaction.clearTranscriptSelection();

    const range = selection.getRangeAt(0);
    if (!evt.currentTarget.contains(range.commonAncestorContainer))
      return;

    const before = range.cloneRange();
    before.selectNodeContents(evt.currentTarget);
    before.setEnd(range.startContainer, range.startOffset);
    const startOffset = before.toString().length;
    const selectedText = range.toString();

    ownerModel.redaction.setTranscriptSelection(
      moment,
      startOffset,
      startOffset + selectedText.length,
      selectedText,
    );
  };

  return (
    <div className="trainer-transcript-block"
      aria-expanded={active}
      data-moment={true}
      data-moment-id={moment.id}>

      <div className="block-summary"
        onClick={evt => ownerModel.handleTranscriptClick(evt, moment)}>

        <span className="block-time">
          <span className="text">{moment?.startTimeLabel || '0:00'}</span>
          <span className="icon"><ArrowRightSVGIcon /></span>
          <span className="text">{moment?.endTimeLabel || '0:00'}</span>
        </span>

        <span className="block-speaker">{moment.speaker?.name}</span>
      </div>

      {description && active &&
        <>
          <div className="block-text" onMouseUp={handleTextSelection}>
            {text}
          </div>

          {selection && (
            <div className="transcript-redaction-actions" role="toolbar" aria-label="Transcript redaction actions">
              <span className="selection-label">“{selection.text}”</span>
              <button
                type="button"
                className="primary-action"
                disabled={ownerModel.redaction.isSaving}
                onClick={() => ownerModel.redaction.redactTranscriptSelection()}>
                Mute &amp; redact
              </button>
              <button
                type="button"
                disabled={ownerModel.redaction.isSaving}
                onClick={() => ownerModel.redaction.redactSpeakerTrack()}>
                Remove speaker track
              </button>
              <button
                type="button"
                className="dismiss-action"
                aria-label="Dismiss transcript selection"
                onClick={ownerModel.redaction.clearTranscriptSelection}>
                ×
              </button>
            </div>
          )}
        </>}
    </div>
  );
});
